import {
  CheckCircle2,
  Clock3,
  FileVideo,
  Loader2,
  XCircle,
} from "lucide-react";

const STATUS_CONFIG = {
  queued: {
    label: "Queued",
    icon: Clock3,
    badge: "bg-amber-50 text-amber-700 ring-amber-200",
    iconStyle: "bg-amber-100 text-amber-700",
  },
  running: {
    label: "Processing",
    icon: Loader2,
    badge: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    iconStyle: "bg-indigo-100 text-indigo-700",
  },
  completed: {
    label: "Ready",
    icon: CheckCircle2,
    badge: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    iconStyle: "bg-emerald-100 text-emerald-700",
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    badge: "bg-red-50 text-red-700 ring-red-200",
    iconStyle: "bg-red-100 text-red-700",
  },
};

function clampProgress(value) {
  const progress = Number(value);
  if (!Number.isFinite(progress)) return 0;
  return Math.min(100, Math.max(0, Math.round(progress)));
}

function formatCreatedAt(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function getJobTitle(job) {
  return (
    job.title ||
    job.file_name ||
    job.video_title ||
    (job.video_id ? `Video ${job.video_id}` : "Video analysis")
  );
}

export default function JobCard({ job, isActive, onClick }) {
  const status = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
  const StatusIcon = status.icon;
  const progress = clampProgress(job.display_progress ?? job.progress);
  const thumbnail = job.thumbnail_url || job.thumbnailUrl || "";
  const createdAt = formatCreatedAt(job.created_at);

  return (
    <button
      type="button"
      onClick={() => onClick(job)}
      aria-pressed={isActive}
      className={`group w-full rounded-xl border p-2.5 text-left transition focus:outline-none focus:ring-4 focus:ring-indigo-100 ${
        isActive
          ? "border-indigo-500 bg-indigo-50/60 shadow-sm"
          : "border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm"
      }`}
    >
      <div className="flex gap-3">
        <div className="relative flex h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-gray-900 to-gray-700">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <FileVideo className="h-6 w-6 text-white/70" aria-hidden="true" />
          )}

          <span
            className={`absolute bottom-1 right-1 rounded-full p-1 ${status.iconStyle}`}
          >
            <StatusIcon
              className={`h-3.5 w-3.5 ${
                job.status === "running" ? "animate-spin" : ""
              }`}
              aria-hidden="true"
            />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {getJobTitle(job)}
            </h3>
            <span
              className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${status.badge}`}
            >
              {status.label}
            </span>
          </div>

          <p className="mt-1 line-clamp-1 text-xs text-gray-500">
            {job.message ||
              (job.status === "completed"
                ? "Three clip suggestions are ready"
                : "Waiting for an update")}
          </p>

          <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-gray-400">
            <span>{createdAt}</span>
            {job.status !== "completed" && job.status !== "failed" && (
              <span>{progress}%</span>
            )}
          </div>
        </div>
      </div>

      {job.status !== "completed" && job.status !== "failed" && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full rounded-full transition-[width] duration-700 ${
              job.status === "running" ? "bg-indigo-600" : "bg-amber-500"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </button>
  );
}
